import {
	JOB_STATUS_CREATED,
	JOB_STATUS_EDITED,
	JOB_STATUS_SORT_ORDER_UPDATED,
	JOB_STATUS_SUSPEND_UPDATED,
	JOB_STATUSES_FETCHED,
	JOB_WARRANTY_REGISTRATION_STATUSES_FETCHED
} from "./actionType";
import {Action} from "redux-actions";
import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import {api} from "../../../api/api";
import {Dispatch} from "react-redux";
import JobStatus from "../../../model/job/JobStatus";
import JobWarrantyRegistrationStatus from "../../../model/job/JobWarrantyRegistrationStatus";

/**
 * Job statuses fetched successful action.
 * @return {Action<JOB_STATUSES_FETCHED>}
 * @param jobStatuses
 */
function jobStatusesFetched(jobStatuses: Array<JobStatus>): Action<JOB_STATUSES_FETCHED> {
	return {type: JOB_STATUSES_FETCHED, payload: {jobStatuses: jobStatuses}};
}

/**
 * Create job status successful action.
 * @return {Action<JOB_STATUS_CREATED>}
 * @param jobStatus
 */
function jobStatusCreated(jobStatus: JobStatus): Action<JOB_STATUS_CREATED> {
	return {type: JOB_STATUS_CREATED, payload: {jobStatus: jobStatus}};
}

/**
 * Create job warranty registration status successful action.
 * @return {Action<JOB_WARRANTY_REGISTRATION_STATUSES_FETCHED>}
 * @param jobWarrantyRegistrationStatuses
 */
function jobWarrantyRegistrationStatusFetched(jobWarrantyRegistrationStatuses: JobWarrantyRegistrationStatus[]): Action<JOB_WARRANTY_REGISTRATION_STATUSES_FETCHED> {
	return {
		type: JOB_WARRANTY_REGISTRATION_STATUSES_FETCHED,
		payload: {jobWarrantyRegistrationStatuses}
	};
}

/**
 * Edit job status successful action.
 * @return {Action<JOB_STATUS_EDITED>}
 * @param jobStatus
 */
function jobStatusEdited(jobStatus: JobStatus): Action<JOB_STATUS_EDITED> {
	return {type: JOB_STATUS_EDITED, payload: {jobStatus: jobStatus}};
}

/**
 * Update job statuses sort order successful action.
 * @return {Action<JOB_STATUS_SORT_ORDER_UPDATED>}
 * @param jobStatuses
 */
function jobStatusesSortOrderUpdated(jobStatuses: Array<JobStatus>): Action<JOB_STATUS_SORT_ORDER_UPDATED> {
	return {type: JOB_STATUS_SORT_ORDER_UPDATED, payload: {jobStatuses}};
}

/**
 * Update job status suspend successful action.
 * @return {Action<JOB_STATUS_SUSPEND_UPDATED>}
 * @param jobStatusId
 * @param disabled
 */
export function jobStatusSuspendUpdated(jobStatusId: string, disabled: boolean): Action<JOB_STATUS_SUSPEND_UPDATED> {
	return {type: JOB_STATUS_SUSPEND_UPDATED, payload: {jobStatusId, disabled}}
}

/**
 * Fetch job statuses from server.
 * @param {OnSuccessData} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchJobStatuses(onSuccess: OnSuccessData<JobStatus[]>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobStatusApi.fetchJobStatuses().then(jobStatuses => {
			dispatch(jobStatusesFetched(jobStatuses));
			onSuccess(jobStatuses);
		}).catch(onFailure);
	};
}

/**
 * Fetch job warranty registration statuses from server.
 */
export function jobWarrantyRegistrationStatuses(onSuccess: OnSuccessData<JobWarrantyRegistrationStatus[]>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobStatusApi.fetchJobWarrantyRegistrationStatus().then(jobWarrantyRegistrationStatus => {
			dispatch(jobWarrantyRegistrationStatusFetched(jobWarrantyRegistrationStatus));
			onSuccess(jobWarrantyRegistrationStatus);
		}).catch(onFailure);
	};
}

/**
 * Disable/enable job status action
 * @param {string} jobStatusId
 * @param {boolean} disabled
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function changeJobStatusSuspend(jobStatusId: string, disabled: boolean, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobStatusApi.changeSuspend(jobStatusId, disabled).then(() => {
			dispatch(jobStatusSuspendUpdated(jobStatusId, disabled));
			dispatch(onSuccess);
		}).catch(onFailure);
	}
}

/**
 * Update job status sort order.
 * @param statuses
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function updateJobStatusSortOrder(statuses: JobStatus[],
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobStatusApi.updateSortOrder(statuses).then(() => {
			dispatch(jobStatusesSortOrderUpdated(statuses));
			dispatch(onSuccess);
		}).catch(onFailure);
	}
}

/**
 * Create job status action.
 * @param {JobStatus} data Job status data object.
 * @param {OnSuccessData<JobStatus>} onSuccess Success callback.
 * @param {OnFailure} onFailure Failure callback.
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function createJobStatus(data: JobStatus, onSuccess: OnSuccessData<JobStatus>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobStatusApi.createJobStatus(data).then((jobStatus) => {
			dispatch(jobStatusCreated(jobStatus));
			onSuccess(jobStatus);
		}).catch(onFailure);
	}
}

/**
 * Edit job status action.
 * @param jobStatusId
 * @param data
 * @param {OnSuccessData<JobStatus>} onSuccess Success callback.
 * @param {OnFailure} onFailure Failure callback.
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function editJobStatus(jobStatusId: string, data: JobStatus, onSuccess: OnSuccessData<JobStatus>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobStatusApi.editJobStatus(jobStatusId, data).then((jobStatus) => {
			dispatch(jobStatusEdited(jobStatus));
			onSuccess(jobStatus);
		}).catch(onFailure);
	}
}
